import createCache from '@emotion/cache';
import { CacheProvider, css } from '@emotion/react';
import styled from '@emotion/styled';
import { createContext, ReactElement, useContext } from 'react';
import { configure, SDK } from 'sdk';

import { AppContainer } from '../components/layout/appContainer';
import { OpenWidgetIcon } from '../components/openWidgetIcon';
import { ThemeProvider } from '../components/themeProvider';
import { UIState } from '../uiState';
import { isMobile } from '../utils/isMobile';
import { ControllerLazy } from './controller/controllerLazy';

configure({
	useProxies: 'ifavailable',
	computedRequiresReaction: true,
	reactionRequiresObservable: true,
});

const SDKCtx = createContext<SDK>(null as unknown as SDK);
const UIStateCtx = createContext<UIState>(null as unknown as UIState);

export const useSDK = (): SDK => useContext(SDKCtx);
export const useUIState = (): UIState => useContext(UIStateCtx);
export const IS_MAC = /MacIntel/.test(navigator.platform);

const styles = css`
	& *::selection {
		background: #338ff8;
		color: #fff;
	}

	& * {
		text-size-adjust: none;
	}
	${!IS_MAC && !isMobile
		? `
		// если клиент кастомизирует скроллбар, то изменения будут видны в блоках виджет-чата
		// visibility: hidden; не гарантирует, что стили клиента не повлияют на стили виджета
		& *::-webkit-scrollbar {
			visibility: hidden !important;
			background-color: initial !important;
			border-radius: initial !important;
			box-shadow: initial !important;
			height: initial !important;
			width: initial !important;
		}
		& *::-webkit-scrollbar-track {
			visibility: hidden !important;
			background-color: initial !important;
			border-radius: initial !important;
			box-shadow: initial !important;
			height: initial !important;
			width: initial !important;
		}
		& *::-webkit-scrollbar-thumb {
			visibility: hidden !important;
			background-color: initial !important;
			border-radius: initial !important;
			box-shadow: initial !important;
			height: initial !important;
			width: initial !important;
		}
	`
		: ''}
`;

const cache = createCache({
	key: 'ncc',
	stylisPlugins: [
		(v): string | undefined =>
			v.value.includes(':') && !/important/.test(v.value) && v.type === 'decl'
				? v.value.slice(0, -1) + ' !important;'
				: undefined,
	],
	speedy: true,
});

export const CHAT_ID = 'ncc-chat';

const App = ({
	hideOpenButton,
	className,
	sdk,
}: {
	sdk: SDK;
	hideOpenButton: boolean;
	className?: string;
}): ReactElement => {
	return (
		<CacheProvider value={cache}>
			<div className={className} id={CHAT_ID}>
				<SDKCtx.Provider value={sdk}>
					<UIStateCtx.Provider value={new UIState(sdk)}>
						<ThemeProvider>
							<AppContainer>
								<ControllerLazy />
								{!hideOpenButton && <OpenWidgetIcon />}
							</AppContainer>
						</ThemeProvider>
					</UIStateCtx.Provider>
				</SDKCtx.Provider>
			</div>
		</CacheProvider>
	);
};

export default styled(App)(styles);
